import { motion } from 'framer-motion'
import { Img } from 'openimg/react'
import { useState } from 'react'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Input } from '#app/components/ui/input.tsx'
import { cn } from '#app/utils/misc.tsx'

interface Sculpture {
	id: string
	title: string
	imageUrl: string | null
}

interface SculptureSelectorProps {
	sculptures: Sculpture[]
	currentSculpture?: Sculpture | null
	onSelect: (sculpture: Sculpture) => void
	onVoiceInput?: () => void
	isRecording?: boolean
	mode?: 'add-photos' | 'remove' | 'edit'
}

export function SculptureSelector({
	sculptures,
	currentSculpture,
	onSelect,
	onVoiceInput,
	isRecording = false,
	mode = 'add-photos',
}: SculptureSelectorProps) {
	const [searchQuery, setSearchQuery] = useState('')
	const [selectedSculpture, setSelectedSculpture] = useState<Sculpture | null>(
		currentSculpture || null,
	)

	// Filter sculptures based on search
	const filteredSculptures = sculptures.filter((sculpture) =>
		sculpture.title.toLowerCase().includes(searchQuery.toLowerCase()),
	)

	const handleSelect = (sculpture: Sculpture) => {
		setSelectedSculpture(sculpture)
		onSelect(sculpture)
	}

	return (
		<div className="mr-11 ml-11 space-y-4">
			{/* Current selected sculpture display */}
			{selectedSculpture && (
				<motion.div
					initial={{ opacity: 0, y: -10 }}
					animate={{ opacity: 1, y: 0 }}
					className="bg-primary/5 border-primary/20 rounded-lg border p-4"
				>
					<div className="flex items-center gap-4">
						<div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-lg bg-gray-100">
							{selectedSculpture.imageUrl ? (
								<Img
									src={selectedSculpture.imageUrl}
									alt={selectedSculpture.title}
									width={160}
									height={160}
									fit="cover"
									className="h-full w-full object-cover"
								/>
							) : (
								<div className="flex h-full items-center justify-center">
									<span className="text-2xl">🎨</span>
								</div>
							)}
						</div>
						<div className="flex-1">
							<p className="text-muted-foreground text-sm">
								Geselecteerd beeld:
							</p>
							<h4 className="font-semibold">{selectedSculpture.title}</h4>
						</div>
						<Icon
							name="check"
							className="text-primary bg-primary/10 h-6 w-6 rounded-full p-1"
						/>
					</div>
				</motion.div>
			)}

			{/* Search input with voice button */}
			<div className="flex gap-2">
				<div className="relative flex-1">
					<Icon
						name="magnifying-glass"
						className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2"
					/>
					<Input
						type="search"
						placeholder="Zoek een beeldhouwwerk..."
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						className="pl-10"
						autoFocus
					/>
				</div>
				{onVoiceInput && (
					<Button
						type="button"
						size="icon"
						variant={isRecording ? 'default' : 'outline'}
						onClick={onVoiceInput}
						className={cn(
							'transition-colors',
							isRecording && 'animate-pulse bg-red-500 hover:bg-red-600',
						)}
						title={isRecording ? 'Stop opname' : 'Start spraakopname'}
					>
						<svg
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="2"
							strokeLinecap="round"
							strokeLinejoin="round"
							className="size-4"
						>
							{isRecording ? (
								// Stop icon
								<rect x="6" y="6" width="12" height="12" />
							) : (
								// Microphone icon
								<>
									<path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
									<path d="M19 10v2a7 7 0 0 1-14 0v-2" />
									<line x1="12" y1="19" x2="12" y2="23" />
									<line x1="8" y1="23" x2="16" y2="23" />
								</>
							)}
						</svg>
					</Button>
				)}
			</div>

			{/* Sculpture grid */}
			<div className="space-y-2">
				<p className="text-muted-foreground text-sm">
					{mode === 'remove'
						? 'Selecteer het beeld dat je wilt verwijderen:'
						: "Selecteer een beeld om foto's aan toe te voegen:"}
				</p>

				{filteredSculptures.length === 0 ? (
					<div className="text-muted-foreground py-8 text-center">
						<p className="mb-2">Geen beeldhouwwerken gevonden</p>
						<p className="text-sm">
							{searchQuery
								? `Geen resultaten voor "${searchQuery}"`
								: 'Er zijn nog geen beeldhouwwerken toegevoegd aan je portfolio'}
						</p>
					</div>
				) : (
					<div className="grid max-h-[400px] grid-cols-2 gap-4 overflow-y-auto pr-2 sm:grid-cols-3 md:grid-cols-4">
						{filteredSculptures.map((sculpture, index) => (
							<motion.button
								key={sculpture.id}
								onClick={() => handleSelect(sculpture)}
								initial={{ opacity: 0, y: 10 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ delay: index * 0.05 }}
								className={cn(
									'relative aspect-square overflow-hidden rounded-lg bg-gray-100 transition-all',
									'hover:ring-primary/50 hover:scale-105 hover:ring-2',
									selectedSculpture?.id === sculpture.id &&
										'ring-primary scale-105 ring-2',
								)}
							>
								{sculpture.imageUrl ? (
									<Img
										src={sculpture.imageUrl}
										alt={sculpture.title}
										width={256}
										height={256}
										fit="cover"
										className="h-full w-full object-cover"
									/>
								) : (
									<div className="flex h-full items-center justify-center bg-gray-200 dark:bg-gray-700">
										<span className="text-3xl">🎨</span>
									</div>
								)}

								{/* Title overlay */}
								<div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3">
									<h3 className="line-clamp-2 text-sm font-semibold text-white">
										{sculpture.title}
									</h3>
								</div>

								{/* Selected indicator */}
								{selectedSculpture?.id === sculpture.id && (
									<div className="bg-primary text-primary-foreground absolute top-2 right-2 rounded-full p-1">
										<Icon name="check" className="h-4 w-4" />
									</div>
								)}
							</motion.button>
						))}
					</div>
				)}
			</div>
		</div>
	)
}
